import { withPluginApi } from 'discourse/lib/plugin-api';
import { ajax } from 'discourse/lib/ajax';
import { getOwner } from 'discourse-common/lib/get-owner';

const BATCH_INTERVAL = 15000;
const MAX_BATCH_SIZE = 20;

let eventBatch = [];
let batchTimer = null;
let sessionId = null;
let currentScrollDepth = 0;
let maxScrollDepth = 0;
let scrollTracked = false;

function generateSessionId() {
  return 'pd_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 12);
}

function getSessionId() {
  if (sessionId) return sessionId;
  try {
    sessionId = sessionStorage.getItem('proxyviews_analytics_sid');
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem('proxyviews_analytics_sid', sessionId);
    }
  } catch (e) {
    sessionId = generateSessionId();
  }
  return sessionId;
}

function getPageSource(path) {
  if (!path) return 'other';
  if (path.includes('/guides/')) return 'guide';
  if (path.includes('/comparisons/')) return 'comparison';
  if (path.includes('/benchmarks/')) return 'benchmark';
  if (path.includes('/reviews/')) return 'review';
  if (path.includes('/t/')) return 'topic';
  if (path.includes('/providers/')) return 'directory';
  return 'other';
}

function parseTarget(path) {
  if (!path) return { targetType: null, targetId: null };

  const patterns = [
    { regex: /\/providers\/([\w-]+)/, type: 'provider' },
    { regex: /\/guides\/([\w-]+)/, type: 'guide' },
    { regex: /\/comparisons\/([\w-]+)/, type: 'comparison' },
    { regex: /\/benchmarks\/([\w-]+)/, type: 'benchmark' },
    { regex: /\/reviews\/([\w-]+)/, type: 'review' },
    { regex: /\/t\/([\w-]+)/, type: 'topic' },
  ];

  for (const pattern of patterns) {
    const match = path.match(pattern.regex);
    if (match) {
      return { targetType: pattern.type, targetId: match[1] };
    }
  }

  return { targetType: null, targetId: null };
}

function queueEvent(eventType, metadata = {}, owner) {
  const path = window.location.pathname;
  const source = getPageSource(path);
  const { targetType, targetId } = parseTarget(path);

  let experiments = {};
  if (owner) {
    const experimentService = owner.lookup?.('service:proxyviews-experiments') || owner.__container__?.lookup('service:proxyviews-experiments');
    if (experimentService) {
      experiments = experimentService.getAllAssignments();
    }
  }

  // Inject active A/B experiments into the payload's metadata
  metadata.experiments = experiments;

  const event = {
    event_type: eventType,
    event_source: source,
    target_type: targetType,
    target_id: targetId,
    session_id: getSessionId(),
    metadata: metadata,
  };

  eventBatch.push(event);

  if (eventBatch.length >= MAX_BATCH_SIZE) {
    flushEvents();
  }
}

function flushEvents() {
  if (eventBatch.length === 0) return;

  const eventsToSend = [...eventBatch];
  eventBatch = [];

  ajax('/proxyviews/analytics/events/batch', {
    type: 'POST',
    contentType: 'application/json',
    data: JSON.stringify({ events: eventsToSend }),
  }).catch((err) => {
    console.warn('[proxyviews-analytics] Batch send failed:', err);
  });
}

function startBatchTimer() {
  if (batchTimer) return;
  batchTimer = setInterval(flushEvents, BATCH_INTERVAL);
}

function stopBatchTimer() {
  if (batchTimer) {
    clearInterval(batchTimer);
    batchTimer = null;
  }
  flushEvents();
}

let _siteSettings = null;

function getSiteSettings(owner) {
  if (!_siteSettings) {
    _siteSettings =
      owner.lookup?.('service:site-settings') ||
      owner.__container__?.lookup('service:site-settings');
  }
  return _siteSettings;
}

function trackPageView(owner) {
  const s = getSiteSettings(owner);
  if (!s?.proxyviews_analytics_enabled) return;
  if (!s?.proxyviews_analytics_track_pageviews) return;

  const path = window.location.pathname;
  const source = getPageSource(path);

  let eventType = 'page_view';
  if (source === 'guide') eventType = 'guide_view';
  if (source === 'comparison') eventType = 'comparison_view';
  if (source === 'benchmark') eventType = 'benchmark_view';

  queueEvent(eventType, {
    path: path,
    title: document.title,
  }, owner);

  currentScrollDepth = 0;
  maxScrollDepth = 0;
  scrollTracked = false;
}

function trackAffiliateClicks(owner) {
  const s = getSiteSettings(owner);
  if (!s?.proxyviews_analytics_enabled) return;
  if (!s?.proxyviews_analytics_track_affiliate_clicks) return;

  const handler = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.href || '';
    const isAffiliate =
      href.includes('/out/') ||
      href.includes('/redirect/') ||
      href.includes('/go/') ||
      link.dataset.affiliate === 'true' ||
      link.classList.contains('affiliate-link');

    if (isAffiliate) {
      const path = window.location.pathname;
      const { targetType, targetId } = parseTarget(path);

      queueEvent('affiliate_click', {
        target_type: (targetType || link.dataset.provider) ? 'provider' : null,
        target_id: targetId || link.dataset.provider || null,
        link_url: href,
        link_text: link.textContent.trim().substring(0, 200),
      }, owner);
    }
  };

  document.addEventListener('click', handler);
  return () => document.removeEventListener('click', handler);
}

function calculateScrollDepth() {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
  if (docHeight <= 0) return 0;
  return Math.min(Math.round((scrollTop / docHeight) * 100), 100);
}

function handleScroll(owner) {
  currentScrollDepth = calculateScrollDepth();

  if (currentScrollDepth > maxScrollDepth) {
    maxScrollDepth = currentScrollDepth;
  }

  const milestones = [25, 50, 75, 90];
  const trackedMilestones = window.__pd_trackedScrollMilestones || new Set();
  window.__pd_trackedScrollMilestones = trackedMilestones;

  for (const milestone of milestones) {
    if (maxScrollDepth >= milestone && !trackedMilestones.has(milestone)) {
      trackedMilestones.add(milestone);

      const path = window.location.pathname;
      const source = getPageSource(path);

      if (
        source === 'guide' ||
        source === 'comparison' ||
        source === 'benchmark' ||
        source === 'directory'
      ) {
        queueEvent('scroll', {
          depth: maxScrollDepth,
          milestone: milestone,
        }, owner);
      }
    }
  }
}

function setupScrollTracking(owner) {
  let scrollTimeout = null;
  const handler = () => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => handleScroll(owner), 150);
  };
  window.addEventListener('scroll', handler, { passive: true });
  return () => {
    window.removeEventListener('scroll', handler);
    if (scrollTimeout) clearTimeout(scrollTimeout);
  };
}

function trackSearchEvents(api, owner) {
  api.onAppEvent('search:trigger', (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) return;

    queueEvent('search', {
      query: searchTerm.trim().substring(0, 200),
    }, owner);
  });
}

export default {
  name: 'proxyviews-analytics',

  initialize(owner) {
    const siteSettings =
      owner.lookup?.('service:site-settings') ||
      owner.__container__?.lookup('service:site-settings');
    if (!siteSettings || !siteSettings.proxyviews_analytics_enabled) return;

    const cleanups = [];

    withPluginApi('1.14.0', (api) => {
      getSessionId();
      startBatchTimer();

      api.onPageChange((url, title) => {
        trackPageView(owner);
      });

      const removeAffiliateListener = trackAffiliateClicks(owner);
      if (removeAffiliateListener) cleanups.push(removeAffiliateListener);

      const removeScrollListener = setupScrollTracking(owner);
      if (removeScrollListener) cleanups.push(removeScrollListener);

      try {
        trackSearchEvents(api, owner);
      } catch (e) {
        // search:trigger event not available in this version
      }

      const beforeUnloadHandler = () => {
        flushEvents();
      };
      window.addEventListener('beforeunload', beforeUnloadHandler);
      cleanups.push(() => window.removeEventListener('beforeunload', beforeUnloadHandler));

      // Note: Discourse's plugin API doesn't have a cleanUp method.
      // The cleanups array is used on beforeunload (flushEvents) above.
      // Listeners live for the page lifetime, which is fine for analytics.
    });
  },
};
